import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MatchingService, type GuideDetails, type GuideEnhancedProfile } from '../../services/matchingService';
import { getSupabaseClient } from '../../lib/supabase';
import { Footer } from '@ui/components/Footer';
import { SeekerHeader } from '@ui/components/SeekerHeader';

type GuideProfileViewProps = {
  guide: GuideDetails;
  matchScore?: number;
  onBack?: () => void;
  onScheduleConsultation?: (guide: GuideDetails) => void;
  hasScheduledConsultation?: boolean;
};

interface GuideReview {
  id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  would_recommend: boolean | null;
  is_anonymous: boolean;
  created_at: string;
  seeker_id: string;
  source: 'platform' | 'off_platform';
  reviewer_name?: string;
}

export const GuideProfileView: React.FC<GuideProfileViewProps> = ({
  guide,
  matchScore,
  onBack,
  onScheduleConsultation,
  hasScheduledConsultation = false,
}) => {
  const location = useLocation();
  const [enhancedProfile, setEnhancedProfile] = useState<GuideEnhancedProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [reviews, setReviews] = useState<GuideReview[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [backgroundChecked, setBackgroundChecked] = useState<boolean | null>(
    typeof guide.backgroundChecked === 'boolean' ? guide.backgroundChecked : null
  );

  // Fetch enhanced profile data
  useEffect(() => {
    if (guide?.guide_id) {
      setIsLoadingProfile(true);
      MatchingService.getGuideEnhancedProfile(guide.guide_id)
        .then((profile: GuideEnhancedProfile | null) => {
          console.log('Fetched enhanced profile for full view:', profile);
          setEnhancedProfile(profile);
        })
        .catch((error: any) => {
          console.error('Error fetching enhanced profile:', error);
        })
        .finally(() => {
          setIsLoadingProfile(false);
        });
    }
  }, [guide?.guide_id]);

  // Fetch reviews for this guide (from both platform and off-platform tables)
  useEffect(() => {
    if (!guide?.guide_id) return;

    const supabase = getSupabaseClient();
    setIsLoadingReviews(true);

    async function fetchAllReviews() {
      try {
        // Fetch platform reviews
        const { data: platformReviews, error: platformError } = await supabase
          .from('guide_reviews')
          .select('id, rating, title, comment, would_recommend, is_anonymous, created_at, seeker_id')
          .eq('guide_id', guide.guide_id)
          .eq('visibility', 'public')
          .order('created_at', { ascending: false });

        if (platformError) {
          console.error('Error fetching platform reviews:', platformError);
        }

        // Fetch off-platform reviews
        const { data: offPlatformReviews, error: offPlatformError } = await supabase
          .from('off_platform_guide_reviews')
          .select('id, rating, title, body, created_at, reviewer_name')
          .eq('guide_id', guide.guide_id)
          .eq('visibility', 'public')
          .eq('is_submitted', true)
          .order('created_at', { ascending: false });

        if (offPlatformError) {
          console.error('Error fetching off-platform reviews:', offPlatformError);
        }

        // Combine and normalize reviews
        const normalizedPlatformReviews: GuideReview[] = (platformReviews || []).map(r => ({
          ...r,
          source: 'platform' as const,
        }));

        const normalizedOffPlatformReviews: GuideReview[] = (offPlatformReviews || []).map(r => ({
          id: r.id,
          rating: r.rating,
          title: r.title,
          comment: r.body,
          would_recommend: null,
          is_anonymous: false,
          created_at: r.created_at,
          seeker_id: '',
          source: 'off_platform' as const,
          reviewer_name: r.reviewer_name,
        }));

        // Combine and sort by date
        const allReviews = [...normalizedPlatformReviews, ...normalizedOffPlatformReviews]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        setReviews(allReviews);
      } catch (err) {
        console.error('Unexpected error loading reviews:', err);
      } finally {
        setIsLoadingReviews(false);
      }
    }

    fetchAllReviews();
  }, [guide?.guide_id]);

  useEffect(() => {
    /*if (typeof guide.backgroundChecked === 'boolean') {
      setBackgroundChecked(guide.backgroundChecked);
      return;
    }

    if (!guide?.guide_id) return;

    const supabase = getSupabaseClient();
    let cancelled = false;

    supabase
      .from('profiles')
      .select('backgroundChecked')
      .eq('id', guide.guide_id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn('Unable to load guide profile background status', error);
          return;
        }
        if (typeof data?.backgroundChecked === 'boolean') {
          setBackgroundChecked(data.backgroundChecked);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Unexpected error loading background status', err);
        }
      });

    return () => {
      cancelled = true;
    };*/
    setBackgroundChecked(true);
  }, [guide?.guide_id, guide.backgroundChecked]);

  const guideName = guide?.name || 'Guide';
  const guideLocation = guide?.location;
  const formattedLocation = MatchingService.formatLocation(guideLocation);
  const matchPercentage = matchScore ? Math.round(matchScore) : undefined;
  const profileImage = enhancedProfile?.image_url;
  const profileBio = enhancedProfile?.about || guide?.bio;

  // Calculate average rating
  const averageRating = reviews.length > 0
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
    : 0;

  // Collect all tags for display
  const allTags = [
    ...(guide.focus_areas || []),
    ...(guide.experience_types || []),
    ...(guide.support_types || []),
    ...(guide.special_groups || []),
    ...(guide.religious_beliefs || []),
  ];
  const filteredTags = allTags.filter(
    (tag): tag is string =>
      typeof tag === 'string' && tag.trim().length > 0 && tag.trim().toLowerCase() !== 'none'
  );

  const isHomePath = location.pathname === '/home';

  return (
    <div className="flex h-screen w-full flex-col overflow-y-auto">
      <div className="relative flex flex-1 w-full flex-col bg-white">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[#FFFCEF]/30 bg-transparent text-xl text-[#FFFCEF] transition hover:bg-[#FFFCEF]/10"
            onClick={onBack}
            aria-label="Go back"
          >
            {'\u2190'}
          </button>
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Desktop back button */}
        <div className="hidden lg:flex px-[120px] pt-8">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-[#304031] hover:text-[#214D3C] transition-colors"
            onClick={onBack}
            aria-label="Go back"
          >
            <span className="text-xl">{'\u2190'}</span>
            <span className="text-base font-medium">Back</span>
          </button>
        </div>

        <main className="flex flex-1 min-h-0 overflow-y-auto lg:overflow-visible px-6 py-6 lg:px-[120px] lg:py-8">
          <div className="mx-auto flex w-full max-w-[1200px] flex-col lg:flex-row gap-8 lg:gap-12 py-8 lg:py-12">
            {/* Left Column - Main Content */}
            <div className="flex flex-col gap-6 lg:w-[60%]">
              {/* Name */}
              <h1 className="text-[56px] leading-[1.1] font-serif text-[#214D3C] tracking-tight ph-mask">
                {guideName}
              </h1>

              {/* Location */}
              {/*<div className="flex items-center gap-3 text-[#214D3C]">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-lg">{formattedLocation}</p>
              </div>*/}

              {/* Role/Profession - if available from bio or other source */}
              {guide.experience_types && guide.experience_types.length > 0 && guide.experience_types[0] && (
                <p className="text-lg text-[#214D3C] ph-mask">
                  {guide.experience_types[0]}
                </p>
              )}

              {/* Tags */}
              {filteredTags.length > 0 && (
                <div className="flex flex-wrap gap-2 ph-mask">
                  {filteredTags.slice(0, 8).map((tag, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-[#8d8d8d] bg-transparent px-3 py-1.5 text-sm text-[#214D3C]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* About Section */}
              {profileBio && (
                <div className="text-lg leading-relaxed text-[#214D3C] ph-mask">
                  <h2 className="mb-3 text-lg font-bold text-[#214D3C]">About</h2>
                  <p className="mb-6">{profileBio}</p>
                </div>
              )}

              {/* Training, qualifications section - placeholder for future */}
              {enhancedProfile && (
                <div className="text-lg leading-relaxed text-[#214D3C]">
                  <h2 className="mb-3 text-lg font-bold text-[#214D3C]">Training, qualifications and experience</h2>
                  <p className="text-[#214D3C]/80 ph-mask">
                    {guideName.split(' ')[0]} has completed rigorous training and professional development.
                  </p>
                </div>
              )}

              {/* Reviews Section — only shown when guide has actual reviews */}
              {!isLoadingReviews && reviews.length > 0 && (
              <div className="text-lg leading-relaxed text-[#214D3C]">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-[#214D3C]">Reviews</h2>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <svg
                            key={star}
                            className={`h-5 w-5 ${star <= Math.round(averageRating) ? 'text-yellow-500' : 'text-gray-300'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-sm text-[#214D3C]/70">
                        {averageRating.toFixed(1)} ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                </div>

                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="rounded-2xl border border-[#2C2731]/20 bg-white/60 p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <svg
                                  key={star}
                                  className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                              ))}
                            </div>
                            {review.would_recommend && (
                              <span className="text-xs text-[#214D3C]/60">• Recommends</span>
                            )}
                          </div>
                          <span className="text-xs text-[#214D3C]/60">
                            {new Date(review.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        {review.title && (
                          <h3 className="mt-2 font-semibold text-[#214D3C] ph-mask">{review.title}</h3>
                        )}
                        {review.comment && (
                          <p className="mt-2 text-sm text-[#214D3C]/80 ph-mask">{review.comment}</p>
                        )}
                        {review.is_anonymous && (
                          <p className="mt-2 text-xs italic text-[#214D3C]/50">Anonymous review</p>
                        )}
                        {review.source === 'off_platform' && review.reviewer_name && (
                          <p className="mt-2 text-xs text-[#214D3C]/50 ph-mask">- {review.reviewer_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
              </div>
              )}

              {isLoadingProfile && (
                <div className="rounded-2xl border border-dashed border-[#2C2731]/40 bg-[#FFFCEF] p-4">
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 w-3/4 rounded bg-[#E8DFBD]" />
                    <div className="h-4 w-1/2 rounded bg-[#E8DFBD]" />
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Image and CTA */}
            <div className="flex flex-col gap-6 pb-8 lg:w-[40%] lg:sticky lg:top-8 lg:self-start">
              {/* Profile Image */}
              <div className="relative w-full aspect-[4/5] rounded-[20px] overflow-hidden bg-[#E8DFBD]">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt={`${guideName} profile`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/assets/guide-avatar.webp";
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <svg className="h-32 w-32 text-[#2C2731]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Schedule Consultation Button */}
              {onScheduleConsultation && !isHomePath && !hasScheduledConsultation && (
                <button
                  type="button"
                  onClick={() => onScheduleConsultation(guide)}
                  className="group flex items-center justify-between bg-[#214D3C] text-[#FFFCEF] px-6 py-4 rounded-full hover:bg-[#1a3d2f] transition-colors"
                >
                  <span className="text-base font-normal">Schedule a Consultation</span>
                  <svg className="h-4 w-4 transform -rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Site-wide footer - only visible on large screens */}
      <div className="hidden lg:block">
        <Footer />
      </div>
    </div>
  );
};
